import { create } from 'zustand';
import type { HelpCenter } from '../types';
import { getHelpCenters } from '../services/api';

interface HelpCenterState {
  // Help centers data
  helpCenters: HelpCenter[];
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchHelpCenters: () => Promise<void>;
  setHelpCenters: (helpCenters: HelpCenter[]) => void;
  clearHelpCenters: () => void;
}

export const useHelpCenterStore = create<HelpCenterState>((set, get) => ({
  // Initial state
  helpCenters: [],
  isLoading: false,
  error: null,
  
  // Fetch help centers from API
  fetchHelpCenters: async () => {
    // Don't refetch if already loading
    if (get().isLoading) {
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const response = await getHelpCenters();
      
      if (response.ok && response.helpCenters) {
        // Sort by updated_at descending (most recent first)
        const sorted = response.helpCenters.sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        set({ helpCenters: sorted, isLoading: false, error: null });
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || 'Failed to fetch help centers';
        set({ error: errorMessage, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching help centers:', error);
      set({ 
        error: 'Failed to fetch help centers', 
        isLoading: false 
      });
    }
  },
  
  // Set help centers directly (useful for optimistic updates)
  setHelpCenters: (helpCenters) => set({ helpCenters }),
  
  // Clear help centers
  clearHelpCenters: () => set({ helpCenters: [], error: null })
}));
